import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Shield, Eye, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { RendPlexLogo } from '../components/RendPlexLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import DarkModeToggle from '../components/DarkModeToggle';
import { Footer } from '../components/LegalDisclaimers';

export default function Privacy() {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B0E14]">
      <nav className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0B0E14] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center gap-2">
              <RendPlexLogo size="md" />
            </Link>
            <div className="flex items-center gap-3">
              <DarkModeToggle />
              <LanguageSwitcher variant="minimal" />
              <Link to="/">
                <Button variant="ghost" className="text-slate-600 dark:text-slate-400">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t('privacy.backToHome')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-8 md:p-12">
          <h1 className="font-primary font-bold text-4xl text-slate-900 dark:text-white mb-2" data-testid="privacy-title">
            {t('privacy.title')}
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mb-8">{t('privacy.lastUpdated')}</p>

          <div className="prose prose-slate dark:prose-invert max-w-none">
            <div className="bg-green-50 dark:bg-green-950/30 border-l-4 border-green-400 p-6 mb-8">
              <h3 className="font-primary font-bold text-lg text-slate-900 dark:text-white mb-2">{t('privacy.yourPrivacy')}</h3>
              <p className="text-slate-700 dark:text-slate-300">{t('privacy.commitment')}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 my-8">
              <div className="border border-slate-200 dark:border-slate-700 rounded-2xl p-6">
                <Shield className="w-8 h-8 text-[#45C04A] mb-3" />
                <h4 className="font-primary font-bold text-slate-900 dark:text-white mb-2">{t('privacy.dataProtection')}</h4>
                <p className="text-sm text-slate-600 dark:text-slate-400">{t('privacy.dataProtectionDesc')}</p>
              </div>
              <div className="border border-slate-200 dark:border-slate-700 rounded-2xl p-6">
                <Eye className="w-8 h-8 text-[#45C04A] mb-3" />
                <h4 className="font-primary font-bold text-slate-900 dark:text-white mb-2">{t('privacy.transparency')}</h4>
                <p className="text-sm text-slate-600 dark:text-slate-400">{t('privacy.transparencyDesc')}</p>
              </div>
              <div className="border border-slate-200 dark:border-slate-700 rounded-2xl p-6">
                <Lock className="w-8 h-8 text-[#45C04A] mb-3" />
                <h4 className="font-primary font-bold text-slate-900 dark:text-white mb-2">{t('privacy.yourControl')}</h4>
                <p className="text-sm text-slate-600 dark:text-slate-400">{t('privacy.yourControlDesc')}</p>
              </div>
            </div>

            <h2 className="font-primary font-bold text-2xl text-slate-900 dark:text-white mt-8 mb-4">{t('privacy.whatWeCollect')}</h2>
            <div className="space-y-4 mb-6">
              <div>
                <h4 className="font-medium text-slate-900 dark:text-white mb-1">{t('privacy.accountInfo')}</h4>
                <p className="text-sm text-slate-600 dark:text-slate-400">{t('privacy.accountInfoDesc')}</p>
              </div>
              <div>
                <h4 className="font-medium text-slate-900 dark:text-white mb-1">{t('privacy.propertyData')}</h4>
                <p className="text-sm text-slate-600 dark:text-slate-400">{t('privacy.propertyDataDesc')}</p>
              </div>
              <div>
                <h4 className="font-medium text-slate-900 dark:text-white mb-1">{t('privacy.usageData')}</h4>
                <p className="text-sm text-slate-600 dark:text-slate-400">{t('privacy.usageDataDesc')}</p>
              </div>
              <div>
                <h4 className="font-medium text-slate-900 dark:text-white mb-1">{t('privacy.technicalData')}</h4>
                <p className="text-sm text-slate-600 dark:text-slate-400">{t('privacy.technicalDataDesc')}</p>
              </div>
            </div>

            <h2 className="font-primary font-bold text-2xl text-slate-900 dark:text-white mt-8 mb-4">{t('privacy.howWeUse')}</h2>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300 mb-6">
              <li>{t('privacy.useItems.analysis')}</li>
              <li>{t('privacy.useItems.ai')}</li>
              <li>{t('privacy.useItems.account')}</li>
              <li>{t('privacy.useItems.improve')}</li>
              <li>{t('privacy.useItems.updates')}</li>
              <li>{t('privacy.useItems.security')}</li>
            </ul>

            <h2 className="font-primary font-bold text-2xl text-slate-900 dark:text-white mt-8 mb-4">{t('privacy.yourRights')}</h2>
            <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-2xl p-6 mb-6">
              <p className="text-slate-700 dark:text-slate-300 mb-3">{t('privacy.rightsIntro')}</p>
              <ul className="list-disc pl-6 space-y-1 text-slate-700 dark:text-slate-300">
                <li><strong>{t('privacy.rights.access')}</strong> {t('privacy.rights.accessDesc')}</li>
                <li><strong>{t('privacy.rights.correct')}</strong> {t('privacy.rights.correctDesc')}</li>
                <li><strong>{t('privacy.rights.delete')}</strong> {t('privacy.rights.deleteDesc')}</li>
                <li><strong>{t('privacy.rights.export')}</strong> {t('privacy.rights.exportDesc')}</li>
                <li><strong>{t('privacy.rights.withdraw')}</strong> {t('privacy.rights.withdrawDesc')}</li>
                <li><strong>{t('privacy.rights.object')}</strong> {t('privacy.rights.objectDesc')}</li>
              </ul>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-4">
                {t('privacy.exerciseRights')}{' '}
                <a href="mailto:support@rendplex.com" className="text-[#45C04A] underline">support@rendplex.com</a>
              </p>
            </div>

            <h2 className="font-primary font-bold text-2xl text-slate-900 dark:text-white mt-8 mb-4">{t('privacy.dataSharing')}</h2>
            <p className="text-slate-700 dark:text-slate-300 mb-3">
              <strong>{t('privacy.noSell')}</strong> {t('privacy.shareIntro')}
            </p>
            <ul className="list-disc pl-6 space-y-1 text-slate-700 dark:text-slate-300 mb-6">
              <li><strong>Google OAuth</strong> - {t('privacy.providers.google')}</li>
              <li><strong>Stripe</strong> - {t('privacy.providers.stripe')}</li>
              <li><strong>OpenAI</strong> - {t('privacy.providers.openai')}</li>
              <li><strong>{t('privacy.providers.cloudLabel')}</strong> - {t('privacy.providers.cloud')}</li>
            </ul>

            <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-2xl p-6 mt-8">
              <h3 className="font-primary font-bold text-slate-900 dark:text-white mb-2">{t('privacy.law25Title')}</h3>
              <p className="text-sm text-slate-700 dark:text-slate-300">{t('privacy.law25Text')}</p>
            </div>

            <div className="mt-8 pt-8 border-t border-slate-200 dark:border-slate-700">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t('privacy.privacyOfficer')}{' '}
                <a href="mailto:support@rendplex.com" className="text-[#45C04A] underline">support@rendplex.com</a>
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
